import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Shared renderer for long legal documents stored as Markdown strings.
 * - GFM tables (subprocessors / retention periods)
 * - Bold, italic, lists, links, headings
 * - Dark-mode-friendly styles aligned with the BPM design system
 *   (bg #111827, orange-500 accents, gray-300 body)
 * - Tables wrapped in horizontal-scroll container for mobile
 */
const components = {
  h1: (props) => (
    <h1
      className="text-3xl sm:text-4xl font-bold mb-6 text-orange-500"
      {...props}
    />
  ),
  h2: (props) => (
    <h2
      className="text-xl sm:text-2xl font-bold mt-10 mb-4 text-white border-l-2 border-orange-500 pl-3"
      {...props}
    />
  ),
  h3: (props) => (
    <h3 className="text-lg font-semibold mt-6 mb-3 text-gray-100" {...props} />
  ),
  p: (props) => (
    <p className="text-gray-300 leading-relaxed mb-4" {...props} />
  ),
  ul: (props) => (
    <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300" {...props} />
  ),
  ol: (props) => (
    <ol
      className="list-decimal pl-6 mb-4 space-y-2 text-gray-300"
      {...props}
    />
  ),
  li: (props) => <li className="leading-relaxed" {...props} />,
  a: (props) => (
    <a
      className="text-orange-400 hover:underline break-words"
      target={props.href?.startsWith('http') ? '_blank' : undefined}
      rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      {...props}
    />
  ),
  strong: (props) => (
    <strong className="font-semibold text-white" {...props} />
  ),
  em: (props) => <em className="text-gray-400 italic" {...props} />,
  hr: () => <hr className="my-8 border-gray-800" />,
  table: (props) => (
    <div className="my-6 -mx-2 sm:mx-0 overflow-x-auto">
      <table
        className="min-w-full border-collapse text-sm text-gray-300"
        {...props}
      />
    </div>
  ),
  thead: (props) => <thead className="bg-[#1F2937]" {...props} />,
  th: (props) => (
    <th
      className="border border-gray-700 px-3 py-2 text-left font-semibold text-white"
      {...props}
    />
  ),
  td: (props) => (
    <td className="border border-gray-800 px-3 py-2 align-top" {...props} />
  ),
  blockquote: (props) => (
    <blockquote
      className="border-l-4 border-orange-500 pl-4 italic text-gray-400 my-4"
      {...props}
    />
  ),
};

export default function LegalDocument({ markdown, testId }) {
  return (
    <article
      data-testid={testId}
      className="legal-document"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdown}
      </ReactMarkdown>
    </article>
  );
}
